import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, CATEGORY_LABELS, STATUS_LABELS as STATUS } from "@/lib/api";
import { ApplicationDialog } from "./ApplicationDialog";

const chip = (on) => `px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] ${on ? "bg-[#1c1418] text-white" : "border border-[#1c1418]/30"}`;

const FilterRow = ({ label, value, setValue, options, items, field, testid }) => (
  <div className="flex flex-wrap items-center gap-2" data-testid={`${testid}-row`}>
    <span className="w-20 font-mono text-[11px] uppercase tracking-[0.2em] text-[#1c1418]/70">{label}</span>
    <button onClick={() => setValue("")} data-testid={`${testid}-all`} className={chip(!value)}>Tutti</button>
    {options.map(([k, v]) => (
      <button key={k} onClick={() => setValue(k)} data-testid={`${testid}-${k}`} className={chip(value === k)}>
        {v} ({items.filter((x) => x[field] === k).length})
      </button>
    ))}
  </div>
);

export const ApplicationsPanel = () => {
  const [items, setItems] = useState([]);
  const [agents, setAgents] = useState([]);
  const [cat, setCat] = useState("");
  const [agent, setAgent] = useState("");
  const [status, setStatus] = useState("");
  const [params, setParams] = useSearchParams();
  const openId = params.get("application");

  const load = useCallback(() => {
    api.get("/admin/applications").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => toast.error("Errore nel caricamento"));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/admin/agents").then((r) => setAgents(Array.isArray(r.data) ? r.data : [])).catch(() => setAgents([]));
  }, []);

  const base = items.filter((x) => (!agent || x.agent_ref === agent) && (!status || x.status === status));
  const counts = base.reduce((a, x) => ({ ...a, [x.category]: (a[x.category] || 0) + 1 }), {});
  const shown = cat ? base.filter((x) => x.category === cat) : base;

  return (
    <div data-testid="applications-panel" className="space-y-3">
      <FilterRow label="Agente" value={agent} setValue={setAgent} items={items} field="agent_ref" testid="app-agent-filter"
        options={agents.map((a) => [a.ref, a.name])} />
      <FilterRow label="Stato" value={status} setValue={setStatus} items={agent ? items.filter((x) => x.agent_ref === agent) : items}
        field="status" testid="app-status-filter" options={Object.entries(STATUS)} />
      <div className="flex flex-wrap items-center gap-2">
        <span className="w-20 font-mono text-[11px] uppercase tracking-[0.2em] text-[#1c1418]/70">Altezza</span>
        <button onClick={() => setCat("")} data-testid="app-filter-all" className={chip(!cat)}>Tutte ({base.length})</button>
        {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
          <button key={k} onClick={() => setCat(k)} data-testid={`app-filter-${k}`}
            className={chip(cat === k)}>{v.it} ({counts[k] || 0})</button>
        ))}
      </div>
      <div className="mt-6 overflow-x-auto">
        <table className="w-full min-w-[760px] text-left" data-testid="applications-table">
          <thead className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#1c1418]/60">
            <tr className="border-b-2 border-[#1c1418]"><th className="py-3">Data</th><th>Nome</th><th>Categoria</th><th>Altezza</th><th>Agente</th><th>Città</th><th>Stato</th><th>Email</th></tr>
          </thead>
          <tbody>
            {shown.map((a) => (
              <tr key={a.id} onClick={() => setParams({ application: a.id })} data-testid={`application-row-${a.id}`}
                className="cursor-pointer border-b border-[#1c1418]/15 transition-colors hover:bg-[#f4f4f4]">
                <td className="py-4 font-mono text-xs">{new Date(a.created_at).toLocaleDateString("it-IT")}</td>
                <td className="font-display text-xl uppercase">{a.first_name} {a.last_name}</td>
                <td className="font-mono text-xs">{CATEGORY_LABELS[a.category]?.it}</td>
                <td className="font-mono text-xs">{a.height} cm</td>
                <td className="font-mono text-xs">{a.agent_name || "—"}</td>
                <td className="text-sm">{a.city || "—"}</td>
                <td><span className={`px-2 py-1 font-mono text-[11px] uppercase ${a.status === "approved" ? "bg-[#1c1418] text-white" : a.status === "rejected" ? "bg-[#1c1418]/10" : "bg-[#f4f4f4]"}`}>{STATUS[a.status]}</span></td>
                <td className="font-mono text-[11px] uppercase">{a.email_status === "sent" ? "inviata" : a.email_status}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!shown.length && <p className="py-12 font-serif text-xl italic" data-testid="applications-empty">Nessuna candidatura con questi filtri.</p>}
      </div>
      {openId && <ApplicationDialog id={openId} onClose={() => setParams({})} onChange={load} statusLabels={STATUS} />}
    </div>
  );
};
